import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import AdminHeader from "../components/AdminHeader";
import NavSidebar from "../components/NavSidebar";
import { usePageMeta } from "../components/DashboardShell";
import InfoTip from "../components/shared/InfoTip";
import { useNotification } from "../contexts/NotificationContext";
import { useFeatures } from "../contexts/FeaturesContext";
import { useTheme } from "../contexts/ThemeContext";
import { useView } from "../contexts/ViewContext";
import {
  getUserDetails,
  getProjectsByClient,
  getMetricsData,
  getGrowthComparison,
} from "../api";
import DashboardTileGrid from "../components/dashboard/DashboardTileGrid";

import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Legend,
} from "recharts";

// jspdf / jspdf-autotable / html2canvas are loaded on demand in downloadPDF
// (kept out of the initial bundle — only needed when exporting a PDF).
import Papa from "papaparse";

/* ----------------------------- helpers ----------------------------- */
const fmtDate = (d) => {
  try {
    const dt = typeof d === "string" ? new Date(d) : d;
    if (Number.isNaN(dt?.getTime?.())) return "";
    return dt.toISOString().slice(0, 10);
  } catch { return ""; }
};
const startOfMonth = (d = new Date()) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth   = (d = new Date()) => new Date(d.getFullYear(), d.getMonth() + 1, 0);

// 2026-07-27 — the feature-tile dashboard is now the default admin experience.
// Previously gated behind ?new_dashboard=1 during rollout; that flag is gone.
// The legacy chat/voice-focused body below is preserved as unreachable code
// for one release so we can revert quickly if anything regresses — delete it
// once /dashboard has been on the tile grid for a stable window.
function DashboardPage() {
  // Persistent chrome (header + sidebar) is provided by <DashboardLayout/> in
  // App.js. This page only declares which nav items to highlight and renders
  // its content — so navigating here from another dashboard-style page no longer
  // tears down and re-mounts the whole shell.
  usePageMeta({ headerActive: 'Dashboard', sidebarActive: 'home' });
  return (
    <main className="flex-1 min-w-0">
      <div className="px-6 sm:px-8 py-8 max-w-7xl mx-auto">
        <DashboardTileGrid />
      </div>
    </main>
  );
}

// eslint-disable-next-line no-unused-vars
function LegacyDashboardPage() {
  const navigate = useNavigate();
  const location = useLocation();
  void location;
  const { showSuccess, showError } = useNotification();
  const { hasFeature } = useFeatures();
  const { isDarkMode } = useTheme();
  const view = useView();
  const {
    selectedClientId: headerClientId,
    selectedProjectId: headerProjectId,
    selectedProject: headerProject,
    showChat, showVoice, showBookings, showGoogleAds, showCdp,
  } = view;

  // Use ViewContext for all state when header is active
  const clients = view.clients;
  const loadingClients = view.loadingClients;
  const [clientsError] = useState("");
  const [localClientId, setLocalClientId] = useState(null);

  // When header has a selection, use it directly — no local override
  const selectedClientId = headerClientId || localClientId;
  const setSelectedClientId = headerClientId ? () => {} : setLocalClientId;

  // showChat, showVoice, showBookings, showGoogleAds come from useView()

  const selectedClient = useMemo(
    () => clients.find((c) => String(c.id) === String(selectedClientId)) || null,
    [clients, selectedClientId]
  );

  const [subs, setSubs] = useState({ is_chat_subscribed: false, is_voice_subscribed: false });
  const [loadingSubs, setLoadingSubs] = useState(false);

  const [localProduct, setLocalProduct] = useState(null);
  // When header is active, product comes from the selected project's type
  const product = headerProject ? headerProject.type : localProduct;
  const setProduct = headerProject ? () => {} : setLocalProduct;

  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [projectsError, setProjectsError] = useState("");
  const [query, setQuery] = useState("");

  // Guard against literal "undefined" from stale prior writes (see AuthContext.safeStore).
  const _rawUsername = localStorage.getItem("username");
  const username = (_rawUsername && _rawUsername !== "undefined") ? _rawUsername : "admin";

  const [localProjectId, setLocalProjectId] = useState(null);
  const selectedProjectId = headerProjectId || localProjectId;
  const setSelectedProjectId = headerProjectId ? () => {} : setLocalProjectId;

  // Single source of truth: dates always come from ViewContext (header global filter)
  const rangeStart = view.rangeStart;
  const rangeEnd = view.rangeEnd;
  const setRangeStart = (v) => view.setDateRange(v, view.rangeEnd);
  const setRangeEnd = (v) => view.setDateRange(view.rangeStart, v);
  const [activeQuickFilter, setActiveQuickFilter] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const exportRef = useRef(null);

  const [metrics, setMetrics] = useState({
    timeSeriesData: [],
    summary: { conversations: 0, messages: 0, bookings: 0 },
  });
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [metricsError, setMetricsError] = useState("");

  const [growth, setGrowth] = useState({
    currentPeriod: { conversations: 0, messages: 0, bookings: 0 },
    previousPeriod:{ conversations: 0, messages: 0, bookings: 0 },
    growth:        { conversations: 0, messages: 0, bookings: 0 },
  });
  const [growthLoading, setGrowthLoading] = useState(false);
  const [growthError, setGrowthError] = useState("");

  const chartsRef = useRef(null);

  // Auto-set local client when no header selection and clients loaded
  useEffect(() => {
    if (!headerClientId && !localClientId && clients.length > 0) {
      setLocalClientId(String(clients[0].id));
    }
  }, [clients, selectedClientId, headerClientId]);

  useEffect(() => {
    if (!selectedClientId) return;
    const loadSubs = async () => {
      try {
        setLoadingSubs(true);
        const resp = await getUserDetails(selectedClientId);
        const data = resp?.data ?? resp ?? {};
        const next = {
          is_chat_subscribed: !!data.is_chat_subscribed,
          is_voice_subscribed: !!data.is_voice_subscribed,
        };
        setSubs(next);
        if (next.is_chat_subscribed) {
          setProduct("chat");
        } else if (next.is_voice_subscribed) {
          setProduct("voice");
        } else {
          setProduct(null);
        }
      } catch (e) {
        setSubs({ is_chat_subscribed: false, is_voice_subscribed: false });
        setProduct(null);
      } finally {
        setLoadingSubs(false);
      }
    };
    loadSubs();
  }, [selectedClientId]);

  useEffect(() => {
    let on = true;
    (async () => {
      try {
        if (!selectedClientId || !product) {
          setProjects([]);
          setSelectedProjectId(null);
          return;
        }
        setLoadingProjects(true);
        setProjectsError("");

        const resp = await getProjectsByClient(selectedClientId, 1, 200, product);
        const root = resp?.data ?? resp ?? {};
        const rows = root?.data ?? root?.rows ?? root?.projects ?? root?.data?.data ?? [];
        if (!on) return;

        const list = Array.isArray(rows) ? rows : [];

        const filtered = list.filter((p) => {
          const t = (p.type || p.project_type || "").toString().toLowerCase();
          return t ? t.includes(product) : true;
        });

        const mapped = filtered.map((p) => ({
          id: String(p.id ?? p.project_id ?? p._id ?? ""),
          name: p.name ?? p.project_name ?? `Project ${p.id ?? ""}`,
          logo: p.logoUrl ?? p.logo ?? null,
        }));

        const sortedAZ = mapped.slice().sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        const firstAlphaId = sortedAZ[0]?.id ?? null;

        setProjects(mapped);
        setSelectedProjectId(firstAlphaId);
      } catch (e) {
        if (!on) return;
        setProjectsError(e?.response?.data?.message || e?.message || "Failed to load projects.");
        setProjects([]);
        setSelectedProjectId(null);
      } finally {
        if (on) setLoadingProjects(false);
      }
    })();
    return () => { on = false; };
  }, [selectedClientId, product]);

  const visibleProjects = useMemo(() => {
    let filtered = projects;
    // If a project is selected in the header, only show that one
    if (headerProjectId) {
      filtered = projects.filter(p => String(p.id) === String(headerProjectId));
    }
    const q = query.trim().toLowerCase();
    if (q) {
      filtered = filtered.filter(p => (p.name || "").toLowerCase().includes(q));
    }
    return filtered;
  }, [projects, query, headerProjectId]);

  const [viewMode, setViewMode] = useState(localStorage.getItem("proj_view") || "grid");
  useEffect(() => localStorage.setItem("proj_view", viewMode), [viewMode]);

  const [sortOrder, setSortOrder] = useState(localStorage.getItem("proj_sort") || "az");
  useEffect(() => localStorage.setItem("proj_sort", sortOrder), [sortOrder]);

  const sortedProjects = useMemo(() => {
    const arr = [...visibleProjects];
    const dir = sortOrder === "az" ? 1 : -1;
    arr.sort((a,b) => dir * (a.name || "").localeCompare(b.name || ""));
    return arr;
  }, [visibleProjects, sortOrder]);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(3);
  useEffect(() => setPage(1), [query, product, projects.length, sortOrder]);
  const totalPages = Math.max(1, Math.ceil(sortedProjects.length / pageSize));
  const pageProjects = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sortedProjects.slice(start, start + pageSize);
  }, [sortedProjects, page, pageSize]);
  const goPage = (p) => setPage(Math.min(Math.max(1, p), totalPages));

  const projectIdForApi = useMemo(() => {
    if (selectedProjectId == null) return null;
    const n = Number(selectedProjectId);
    return Number.isNaN(n) ? selectedProjectId : n;
  }, [selectedProjectId]);

  const loadMetrics = async (forceRefresh = false) => {
    if (projectIdForApi == null) return;
    setMetricsLoading(true); setMetricsError("");
    try {
      const params = { start_date: rangeStart, end_date: rangeEnd };
      if (forceRefresh) params.force_refresh = true;
      const resp = await getMetricsData(projectIdForApi, params);
      const data = resp?.data ?? resp ?? {};
      setMetrics({
        timeSeriesData: Array.isArray(data.timeSeriesData) ? data.timeSeriesData : [],
        summary: data.summary || { conversations: 0, messages: 0, bookings: 0 },
      });
    } catch (e) {
      setMetricsError(e?.message || "Failed to fetch metrics data");
      setMetrics({ timeSeriesData: [], summary: { conversations: 0, messages: 0, bookings: 0 } });
    } finally { setMetricsLoading(false); }
  };

  const loadGrowth = async (forceRefresh = false) => {
    if (projectIdForApi == null) return;
    const curStart = new Date(rangeStart);
    const curEnd   = new Date(rangeEnd);
    const days     = Math.max(1, Math.ceil((curEnd - curStart) / (1000*60*60*24)) + 1);
    const prevEnd  = new Date(curStart); prevEnd.setDate(prevEnd.getDate() - 1);
    const prevStart= new Date(prevEnd);  prevStart.setDate(prevStart.getDate() - (days - 1));
    setGrowthLoading(true); setGrowthError("");
    try {
      const params = {
        current_start: fmtDate(curStart), current_end: fmtDate(curEnd),
        previous_start: fmtDate(prevStart), previous_end: fmtDate(prevEnd),
      };
      if (forceRefresh) params.force_refresh = true;
      const resp = await getGrowthComparison(projectIdForApi, params);
      const data = resp?.data ?? resp ?? {};
      setGrowth({
        currentPeriod: data.currentPeriod || { conversations: 0, messages: 0, bookings: 0 },
        previousPeriod:data.previousPeriod|| { conversations: 0, messages: 0, bookings: 0 },
        growth:        data.growth        || { conversations: 0, messages: 0, bookings: 0 },
      });
    } catch (e) {
      setGrowthError(e?.message || "Failed to fetch growth comparison");
      setGrowth({
        currentPeriod: { conversations: 0, messages: 0, bookings: 0 },
        previousPeriod:{ conversations: 0, messages: 0, bookings: 0 },
        growth:        { conversations: 0, messages: 0, bookings: 0 },
      });
    } finally { setGrowthLoading(false); }
  };

  useEffect(() => { loadMetrics(); loadGrowth(); }, [projectIdForApi, rangeStart, rangeEnd]);

  const chartData = useMemo(() => {
    return (metrics.timeSeriesData || []).map((r) => ({
      date: r.date, conversations: r.conversations, messages: r.messages, reservations: 0,
    }));
  }, [metrics.timeSeriesData]);

  const downloadCSV = () => {
    const rows = [["date","conversations","messages","reservations (coming soon)"],
      ...chartData.map((r) => [r.date, r.conversations, r.messages, ""])];
    const csv  = Papa.unparse(rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url; a.download = `metrics_${rangeStart}_to_${rangeEnd}.csv`; a.click(); URL.revokeObjectURL(url);
  };

  const downloadPDF = async () => {
    const [{ default: jsPDF }, { default: html2canvas }, { default: autoTable }] = await Promise.all([
      import("jspdf"),
      import("html2canvas"),
      import("jspdf-autotable"),
    ]);
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    doc.setFontSize(16); doc.text(`Metrics Report`, 40, 40);
    doc.setFontSize(11); doc.text(`Range: ${rangeStart} to ${rangeEnd}`, 40, 58);
    if (chartsRef.current) {
      const canvas   = await html2canvas(chartsRef.current, { backgroundColor: "#ffffff", scale: 2 });
      const imgData  = canvas.toDataURL("image/png");
      const pageW    = doc.internal.pageSize.getWidth();
      const maxW     = pageW - 80;
      const ratio    = canvas.width / canvas.height;
      const imgW     = maxW;
      const imgH     = imgW / ratio;
      doc.addImage(imgData, "PNG", 40, 80, imgW, imgH);
      const tableY = 80 + imgH + 20;
      autoTable(doc, {
        startY: tableY,
        head: [["Metric", "Count"]],
        body: [
          ["Conversations", metrics.summary.conversations],
          ["Messages", metrics.summary.messages],
          ["Reservations (Coming Soon)", ""],
        ],
        styles: { fontSize: 10 }, headStyles: { fillColor: [50,177,255] }, theme: "grid",
        margin: { left: 40, right: 40 },
      });
      const afterY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 16 : tableY + 16;
      autoTable(doc, {
        startY: afterY,
        head: [["Date","Conversations","Messages","Reservations (Coming Soon)"]],
        body: chartData.map((r) => [r.date, r.conversations, r.messages, ""]),
        styles: { fontSize: 9 }, headStyles: { fillColor: [71,83,85] }, theme: "striped",
        margin: { left: 40, right: 40 },
      });
    }
    doc.save(`metrics_${rangeStart}_to_${rangeEnd}.pdf`);
  };

  /* ----------------------------- UI bits ----------------------------- */
  const StatCard = ({ title, value, subtitle, variant = "blue", icon = "chat", tooltip, loading = false }) => {
    const variants = {
      // Primary accent - bold brand cyan for KEY metrics (1-2 most important)
      primary:"from-[#32B1FF] to-blue-600",     // Bold brand cyan/blue - use sparingly!
      // Secondary accent - for high-value metrics like bookings/conversions  
      success:"from-teal-500 to-cyan-600",      // Teal/cyan for success (stays in blue family)
      // Muted variants - for supporting/secondary metrics (lighter blue tones)
      muted:"from-sky-400 to-blue-500",         // Sky blue - softer than primary
      subtle:"from-blue-400 to-indigo-500",     // Blue to indigo
      disabled:"from-slate-400 to-blue-400/50 dark:from-slate-500 dark:to-blue-500/40", // Faded blue-gray
      // Legacy mappings (for backward compatibility)
      cyan:"from-[#32B1FF] to-blue-600", blue:"from-[#32B1FF] to-blue-600",
      teal:"from-teal-500 to-cyan-600", mint:"from-teal-500 to-cyan-600",
      sky:"from-sky-400 to-blue-500", aqua:"from-sky-400 to-blue-500",
      emerald:"from-teal-500 to-cyan-600", rose:"from-[#32B1FF] to-blue-600",
      indigo:"from-blue-400 to-indigo-500", amber:"from-sky-400 to-blue-500",
      slate:"from-slate-400 to-blue-400/50 dark:from-slate-500 dark:to-blue-500/40",
    };
    const icons = {
      chat:<path d="M21 15a4 4 0 0 1-4 4H7l-4 4V5a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />,
      bars:(<> <path d="M4 20V10" /><path d="M10 20V4" /><path d="M16 20v-6" /><path d="M22 20H2" /> </>),
      users:(<> <path d="M12 12a5 5 0 1 0-5-5 5 5 0 0 0 5 5Z" /><path d="M20 22a8 8 0 0 0-16 0" /> </>),
      msg:(<> <rect x="3" y="4" width="18" height="16" rx="2" /><path d="M7 8h10M7 12h6" /> </>),
    };
    return (
      <div className={`relative rounded-2xl p-6 shadow-sm border bg-gradient-to-br ${variants[variant] ?? variants.blue} text-white min-h-[140px] flex flex-col`}>
        {/* Wrap decorative icon in its own overflow-hidden container so tooltip isn't clipped */}
        <div className="absolute inset-0 overflow-hidden rounded-2xl pointer-events-none">
          <div className="absolute -top-6 -right-6 opacity-10 dark:opacity-5">
            <svg width="100" height="100" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
              {icons[icon] ?? icons.chat}
            </svg>
          </div>
        </div>
        <div className="flex items-center gap-1.5 relative z-20">
          <div className="text-[11px] uppercase tracking-wide font-semibold">{title}</div>
          {tooltip && <InfoTip text={tooltip} iconClassName="text-white/70 hover:text-white" />}
        </div>
        {loading ? (
          <div className="mt-2 flex items-center gap-2 relative z-10">
            <svg className="animate-spin h-6 w-6 text-white/80" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="text-lg text-white/70">Loading...</span>
          </div>
        ) : (
          <div className="mt-2 text-3xl font-extrabold drop-shadow-sm relative z-10">{value}</div>
        )}
        {subtitle && <div className="text-xs mt-1.5 text-white/80 relative z-10">{subtitle}</div>}
      </div>
    );
  };

  // Growth Badge with dynamic color based on value (positive=green, negative=red, zero=gray)
  const GrowthBadge = ({ label, value, title }) => {
    const numValue = Number(value) || 0;
    const isPositive = numValue > 0;
    const isNegative = numValue < 0;
    
    // Color classes based on value
    const colorClasses = isPositive
      ? "bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400"
      : isNegative
      ? "bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400"
      : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400";
    
    // Arrow icon
    const ArrowIcon = isPositive ? (
      <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
      </svg>
    ) : isNegative ? (
      <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
      </svg>
    ) : null;

    return (
      <span 
        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[12px] font-semibold transition-colors ${colorClasses}`}
        title={title}
      >
        {ArrowIcon}
        {label}: {numValue >= 0 ? "+" : ""}{numValue}%
      </span>
    );
  };

  /* -------------------------- Product gate -------------------------- */
  const productBlocked = !!view.selectedProjectId && !!product && (
    (product === "chat" && !view.showChat) ||
    (product === "voice" && !view.showVoice)
  );
  useEffect(() => {
    if (!productBlocked) return;
    setMetrics({ timeSeriesData: [], summary: { conversations: 0, messages: 0, bookings: 0 } });
    setGrowth({
      currentPeriod: { conversations: 0, messages: 0, bookings: 0 },
      previousPeriod:{ conversations: 0, messages: 0, bookings: 0 },
      growth:        { conversations: 0, messages: 0, bookings: 0 },
    });
    setMetricsError("");
    setGrowthError("");
  }, [productBlocked]);

  // When the current product isn't available for the selected project, swap
  // to the other AI product if it is (chat↔voice). We do NOT redirect the
  // user away from /dashboard to a different route — the user clicked
  // Dashboard on purpose and should see the "Chat/Voice not enabled for
  // this property" empty state below, not be silently pushed to /bookings.
  // Removed 2026-07-13 — was overriding the intended empty state UX.
  useEffect(() => {
    if (!productBlocked) return;
    if (product === "chat" && view.showVoice) { setProduct("voice"); return; }
    if (product === "voice" && view.showChat) { setProduct("chat"); return; }
  }, [productBlocked, product, view.showChat, view.showVoice]);

  if (productBlocked) {
    const productLabel = product === "voice" ? "Voice" : "Chat";
    return (
      <div className="bg-[#f7f8fa] dark:bg-gray-950 min-h-screen transition-colors duration-200">
        <AdminHeader activePage="Dashboard" />
        <div className="flex">
          <NavSidebar activePage="home" />
          <main className="flex-1 min-w-0">
            <div className="px-10 py-12">
              <div className="max-w-xl mx-auto bg-white dark:bg-gray-900 rounded-xl border dark:border-gray-800 p-8 text-center shadow-sm">
                <svg className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-700 mb-4" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {productLabel} is not enabled for this property
                </h2>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  {view.clientDisplayName ? `"${view.clientDisplayName}" does not have the ${productLabel} feature enabled.` : `The selected project does not have the ${productLabel} feature enabled.`} Switch to another property in the header, or ask an admin to enable it.
                </p>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  /* -------------------------- Main render -------------------------- */
  return (
    <div className="bg-[#f7f8fa] dark:bg-gray-950 min-h-screen transition-colors duration-200">
      <AdminHeader activePage="Dashboard" />

      <div className="flex">
        <NavSidebar activePage="home" />

        {/* Main */}
        <main className="flex-1">
          <div className="px-4 md:px-6 lg:px-10 py-6 space-y-6">


            {/* 1) SEARCH (top) — hidden when header client is selected */}
            {!headerClientId && <div className="bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 p-4 flex flex-col md:flex-row items-stretch md:items-center gap-3">
              <div className="relative flex-1">
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search projects…"
                  className="w-full border dark:border-gray-700 rounded-lg px-10 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#32B1FF] bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                />
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/>
                </svg>
              </div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setQuery("")} className="px-4 py-2 rounded-lg border dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100">
                  Clear
                </button>
              </div>
            </div>}

            {/* 2) PROJECT LIST (Grid/Table with pagination, sorting, no scroll) — hidden when header client is selected */}
            {!headerClientId && <div className="bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 p-6">
              {product && (
                <>
                  {/* Header / actions */}
                  <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4">
                    {loadingProjects && (
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        Loading {product} projects…
                      </div>
                    )}
                    
                    {projectsError && (
                      <div className="text-sm text-red-600 dark:text-red-400">
                        {projectsError}
                      </div>
                    )}

                    <div className="md:ml-auto flex flex-wrap items-center gap-2">
                      <label className="text-xs text-gray-500 dark:text-gray-400">View</label>
                      <div className="inline-flex border dark:border-gray-700 rounded-lg overflow-hidden">
                        <button
                          type="button"
                          onClick={() => setViewMode("grid")}
                          className={`px-3 py-1.5 text-sm ${viewMode==="grid" ? "bg-slate-900 dark:bg-slate-700 text-white" : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"}`}
                        >
                          Grid
                        </button>
                        <button
                          type="button"
                          onClick={() => setViewMode("table")}
                          className={`px-3 py-1.5 text-sm border-l dark:border-gray-700 ${viewMode==="table" ? "bg-slate-900 dark:bg-slate-700 text-white" : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"}`}
                        >
                          Table
                        </button>
                      </div>

                      {/* Sort control */}
                      <label className="text-xs text-gray-500 dark:text-gray-400 ml-3">Sort</label>
                      <select
                        className="border dark:border-gray-700 rounded-lg px-2 py-1 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                        value={sortOrder}
                        onChange={(e) => { setSortOrder(e.target.value); setPage(1); }}
                        title="Sort projects by name"
                      >
                        <option value="az">A–Z</option>
                        <option value="za">Z–A</option>
                      </select>

                      <label className="text-xs text-gray-500 dark:text-gray-400 ml-3">Per page</label>
                      <select
                        className="border dark:border-gray-700 rounded-lg px-2 py-1 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                        value={pageSize}
                        onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                      >
                        {[3,6,12,24,48].map(n => <option key={n} value={n}>{n}</option>)}
                      </select>
                    </div>
                  </div>

                  {/* Empty state */}
                  {!loadingProjects && !projectsError && sortedProjects.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-16 text-center text-gray-500 dark:text-gray-400">
                      <svg className="w-12 h-12 mb-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="7" width="18" height="13" rx="2"/>
                        <path d="M16 3v4M8 3v4M8 17v1M16 17v1M12 17v1"/>
                      </svg>
                      <p className="font-semibold">No {product} projects</p>
                      <p className="text-sm">Try adjusting your search or switch product type above.</p>
                    </div>
                  )}

                  {/* Grid view */}
                  {viewMode === "grid" && pageProjects.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                      {pageProjects.map((project) => {
                        const isSelected = selectedProjectId === project.id;
                        return (
                          <div
                            key={project.id}
                            className={`group relative bg-white dark:bg-gray-800 rounded-xl shadow p-5 border dark:border-gray-700 transition
                              ${isSelected ? "border-green-500 dark:border-green-600 ring-1 ring-green-400/30"
                                           : "hover:border-[#32B1FF] hover:shadow-md"}`}
                          >
                            <div className="flex items-start gap-4">
                              <div className="h-12 w-12 rounded-xl bg-[#EAF6FF] dark:bg-blue-900/30 border border-[#C7E7FF] dark:border-blue-800 flex items-center justify-center overflow-hidden flex-shrink-0">
                                <img 
                                  src={project.logo || "/Rigsby.png"} 
                                  alt={project.name}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.src = "/Rigsby.png";
                                  }}
                                />
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-start justify-between gap-2 mb-1">
                                  <div className="font-extrabold text-lg text-gray-900 dark:text-gray-100 truncate flex-1">{project.name}</div>
                                  {isSelected && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 whitespace-nowrap flex-shrink-0">
                                      <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M20 6L9 17l-5-5" />
                                      </svg>
                                      Active
                                    </span>
                                  )}
                                </div>
                                <div className="text-[13px] text-gray-500 dark:text-gray-400">Tap to view transcripts and conversation logs</div>
                                <div className="mt-3 flex gap-2">
                                  {(() => {
                                    const projTranscriptsDisabled =
                                      (project.type === "chat" && !view.showChat) ||
                                      (project.type === "voice" && !view.showVoice);
                                    return (
                                      <button
                                        type="button"
                                        disabled={projTranscriptsDisabled}
                                        onClick={() => {
                                          if (projTranscriptsDisabled) return;
                                          navigate(`/client/${project.id}`, {
                                            state: { projectName: project.name, projectLogo: project.logo },
                                          });
                                        }}
                                        title={projTranscriptsDisabled
                                          ? `${project.type === "voice" ? "Voice" : "Chat"} is not enabled for this property`
                                          : undefined}
                                        className={`px-4 py-2 rounded-lg text-[12px] font-semibold shadow-sm transition ${
                                          projTranscriptsDisabled
                                            ? "bg-gray-200 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                                            : "bg-[#32B1FF] text-white hover:bg-[#259ae6]"
                                        }`}
                                      >
                                        View transcripts
                                      </button>
                                    );
                                  })()}
                                  <button
                                    type="button"
                                    onClick={() => { setSelectedProjectId(project.id); }}
                                    className="px-3 py-2 rounded-lg border dark:border-gray-700 text-[12px] hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100"
                                    title="Use this project for the analytics below"
                                  >
                                    See analytics
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Table view */}
                  {viewMode === "table" && pageProjects.length > 0 && (
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead className="sticky top-0 bg-slate-50 dark:bg-gray-800 border-b dark:border-gray-700 text-xs text-slate-600 dark:text-slate-400">
                          <tr>
                            <th className="text-left px-3 py-2">Project</th>
                            <th className="text-left px-3 py-2">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pageProjects.map((p) => (
                            <tr key={p.id} className="border-b dark:border-gray-700 last:border-0 hover:bg-slate-50/60 dark:hover:bg-gray-800/60">
                              <td className="px-3 py-2">
                                <div className="flex items-center gap-3">
                                  <div className="h-10 w-10 rounded-lg bg-[#EAF6FF] dark:bg-blue-900/30 border border-[#C7E7FF] dark:border-blue-800 flex items-center justify-center overflow-hidden flex-shrink-0">
                                    <img 
                                      src={p.logo || "/Rigsby.png"} 
                                      alt={p.name}
                                      className="w-full h-full object-cover"
                                      onError={(e) => {
                                        e.target.src = "/Rigsby.png";
                                      }}
                                    />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <div className="font-semibold text-gray-900 dark:text-gray-100">{p.name}</div>
                                      {selectedProjectId === p.id && (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-green-100 text-green-700">
                                          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M20 6L9 17l-5-5" />
                                          </svg>
                                          Selected
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-3 py-2">
                                <div className="flex gap-2">
                                  {(() => {
                                    const projTranscriptsDisabled =
                                      (p.type === "chat" && !view.showChat) ||
                                      (p.type === "voice" && !view.showVoice);
                                    return (
                                      <button
                                        disabled={projTranscriptsDisabled}
                                        onClick={() => {
                                          if (projTranscriptsDisabled) return;
                                          navigate(`/client/${p.id}`, {
                                            state: { projectName: p.name, projectLogo: p.logo },
                                          });
                                        }}
                                        title={projTranscriptsDisabled
                                          ? `${p.type === "voice" ? "Voice" : "Chat"} is not enabled for this property`
                                          : undefined}
                                        className={`px-3 py-1.5 rounded-lg text-[12px] font-semibold shadow-sm transition ${
                                          projTranscriptsDisabled
                                            ? "bg-gray-200 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                                            : "bg-[#32B1FF] text-white hover:bg-[#259ae6]"
                                        }`}
                                      >
                                        View transcripts
                                      </button>
                                    );
                                  })()}
                                  <button
                                    onClick={() => setSelectedProjectId(p.id)}
                                    className="px-3 py-1.5 rounded-lg border dark:border-gray-700 text-[12px] hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100"
                                    title="Use this project for the analytics below"
                                  >
                                    See analytics
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Pagination */}
                  {sortedProjects.length > 0 && (
                    <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        Showing {(page-1)*pageSize+1}–{Math.min(page*pageSize, sortedProjects.length)} of {sortedProjects.length}
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          className="px-3 py-1.5 border dark:border-gray-700 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100"
                          onClick={() => goPage(page-1)}
                          disabled={page===1}
                        >
                          Prev
                        </button>
                        {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
                          const start = Math.max(1, Math.min(page - 3, totalPages - 6));
                          const n = start + i;
                          if (n > totalPages) return null;
                          return (
                            <button
                              key={n}
                              onClick={() => goPage(n)}
                              className={`px-3 py-1.5 border dark:border-gray-700 rounded-lg text-sm ${n===page ? "bg-slate-900 dark:bg-slate-700 text-white" : "hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100"}`}
                            >
                              {n}
                            </button>
                          );
                        })}
                        <button
                          className="px-3 py-1.5 border dark:border-gray-700 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100"
                          onClick={() => goPage(page+1)}
                          disabled={page===totalPages}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>}

            {/* Selected project indicator — compact, no extra spacing */}
            {headerProject && (
              <div className="flex items-center gap-2.5 mt-3">
                <div className="w-8 h-8 rounded-lg overflow-hidden shrink-0">
                  <img
                    src={headerProject.logoUrl || (headerProject.logo ? `https://storage.googleapis.com/insiderperks_dashboard_bucket/${headerProject.logo}` : "/Rigsby.png")}
                    alt={headerProject.name}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.src = "/Rigsby.png"; }}
                  />
                </div>
                <div>
                  <h2 className="text-base font-bold text-gray-900 dark:text-gray-100 leading-tight">{headerProject.name}</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 leading-tight">{headerProject.client_display_name || headerProject.type}</p>
                </div>
              </div>
            )}

            {/* View transcripts CTA for the currently-selected project — mirrors client dashboard */}
            {selectedProjectId && (() => {
              const transcriptsDisabled =
                (product === "chat" && !view.showChat) ||
                (product === "voice" && !view.showVoice) ||
                !product;
              const disabledReason = product === "voice"
                ? "Voice is not enabled for this property"
                : "Chat is not enabled for this property";
              const proj = headerProject || projects.find((p) => String(p.id) === String(selectedProjectId));
              return (
                <div className="flex justify-end">
                  <button
                    type="button"
                    disabled={transcriptsDisabled}
                    onClick={() => {
                      if (transcriptsDisabled) return;
                      navigate(`/client/${selectedProjectId}`, {
                        state: {
                          projectType: product,
                          projectName: proj?.name,
                          projectLogo: proj?.logo,
                        },
                      });
                    }}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold shadow-sm transition ${
                      transcriptsDisabled
                        ? "bg-gray-200 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                        : "bg-[#32B1FF] text-white hover:bg-[#259ae6]"
                    }`}
                    title={transcriptsDisabled ? disabledReason : `Open ${product === "voice" ? "call" : "chat"} transcripts for the selected project`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h7l5 5v11a2 2 0 01-2 2z" />
                    </svg>
                    View transcripts
                  </button>
                </div>
              );
            })()}

            {/* 3) ANALYTICS SUMMARY CARDS */}
            {product === "voice" ? (
              /* Voice Analytics Cards */
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                <StatCard 
                  title="TOTAL CALLS" 
                  value={metrics.summary.conversations.toLocaleString()} 
                  subtitle="Selected period" 
                  variant="primary" 
                  loading={metricsLoading}
                  tooltip="This shows the total number of voice calls received during the selected time period. Each incoming call is counted as one call."
                />
                <StatCard 
                  title="CALL TRANSFERS" 
                  value={(metrics.summary.transfers ?? 0).toLocaleString()} 
                  subtitle="To live agents" 
                  variant="muted" 
                  icon="bars"
                  loading={metricsLoading}
                  tooltip="This shows how many calls were transferred to a live agent or another department. Detected by analyzing conversation content for transfer-related messages."
                />
                <StatCard 
                  title="BOOKING CONVERSIONS" 
                  value={(metrics.summary.bookings ?? 0).toLocaleString()} 
                  subtitle="Confirmed bookings" 
                  variant="success" 
                  icon="msg"
                  loading={metricsLoading}
                  tooltip="This shows the number of successful bookings or appointments made through voice calls. Detected by analyzing conversation content for booking confirmations."
                />
                <StatCard 
                  title="TOTAL RESERVATIONS" 
                  value="Coming Soon" 
                  subtitle="Feature in development" 
                  variant="disabled" 
                  icon="users"
                  loading={false}
                  tooltip="Reservation tracking is coming soon. This will show the total number of reservations made through your voice assistant."
                />
              </div>
            ) : (
              /* Chat Analytics Cards */
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                <StatCard 
                  title="TOTAL CONVERSATIONS" 
                  value={metrics.summary.conversations.toLocaleString()} 
                  subtitle="Selected period" 
                  variant="primary" 
                  loading={metricsLoading}
                  tooltip="This shows how many separate chats or conversations customers started during the selected time period. Each time someone begins a new chat, it counts as one conversation."
                />
                <StatCard 
                  title="TOTAL MESSAGES" 
                  value={metrics.summary.messages.toLocaleString()} 
                  subtitle="All messages exchanged" 
                  variant="muted" 
                  icon="bars"
                  loading={metricsLoading}
                  tooltip="This is the total number of messages exchanged across all conversations. It includes both messages sent by your chatbot and messages sent by your customers. More messages can mean more detailed interactions."
                />
                <StatCard 
                  title="AVG MESSAGES/CHAT" 
                  value={Math.max(0, Math.round((metrics.summary.messages || 0) / Math.max(1, metrics.summary.conversations || 0))).toLocaleString()} 
                  subtitle="Messages per conversation" 
                  variant="muted" 
                  icon="msg"
                  loading={metricsLoading}
                  tooltip="This tells you the average number of messages in each conversation. A higher number means customers are having longer, more engaged conversations. For example, if this is 8, it means each chat typically has about 8 back-and-forth messages."
                />
                <StatCard 
                  title="TOTAL RESERVATIONS" 
                  value="Coming Soon" 
                  subtitle="Feature in development" 
                  variant="disabled" 
                  icon="users"
                  loading={false}
                  tooltip="Reservation tracking is coming soon. This will show the total number of reservations made through your chatbot."
                />
              </div>
            )}

            {/* Controls row (project selector + dates + export) — hidden when header controls active */}
            {!headerClientId && <div className="bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 p-4">
              
              {/* All-in-one compact row */}
              <div className="flex flex-col lg:flex-row gap-3 items-start lg:items-center">
                {/* Compact Project Dropdown */}
                <div className="relative flex items-center gap-2 min-w-[200px] lg:min-w-[220px]">
                  {selectedProjectId && projects.find(p => p.id === selectedProjectId) && (
                    <img 
                      src={projects.find(p => p.id === selectedProjectId)?.logo || "/Rigsby.png"} 
                      alt=""
                      className="w-5 h-5 rounded-full object-cover flex-shrink-0"
                      onError={(e) => { e.target.src = "/Rigsby.png"; }}
                    />
                  )}
                  <select
                    value={selectedProjectId || ""}
                    onChange={(e) => setSelectedProjectId(e.target.value ? Number(e.target.value) : null)}
                    className="flex-1 text-sm border-0 bg-transparent text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-0 pr-8 font-medium cursor-pointer"
                  >
                    <option value="">Select project...</option>
                    {projects.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                  <svg className="w-4 h-4 text-gray-400 absolute right-0 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>

                {/* Vertical Divider */}
                <div className="hidden lg:block h-6 w-px bg-gray-200 dark:bg-gray-700"></div>

                {/* Compact Date Inputs + Quick Select Combined */}
                <div className="flex flex-wrap items-center gap-2">
                  {/* Date Range */}
                  <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <input
                      type="date"
                      className="w-[110px] text-xs bg-transparent border-0 focus:outline-none text-gray-900 dark:text-gray-100 p-0"
                      value={rangeStart}
                      max={rangeEnd}
                      onChange={(e) => { setRangeStart(e.target.value); setActiveQuickFilter(null); }}
                    />
                    <span className="text-gray-400">→</span>
                    <input
                      type="date"
                      className="w-[110px] text-xs bg-transparent border-0 focus:outline-none text-gray-900 dark:text-gray-100 p-0"
                      value={rangeEnd}
                      min={rangeStart}
                      onChange={(e) => { setRangeEnd(e.target.value); setActiveQuickFilter(null); }}
                    />
                  </div>

                  {/* Divider */}
                  <div className="h-6 w-px bg-gray-200 dark:bg-gray-700"></div>

                  {/* Quick Select Pills */}
                  <button
                    type="button"
                    onClick={() => {
                      const today = new Date();
                      const dayOfWeek = today.getDay();
                      const startOfWeek = new Date(today);
                      startOfWeek.setDate(today.getDate() - dayOfWeek);
                      setRangeStart(fmtDate(startOfWeek));
                      setRangeEnd(fmtDate(today));
                      setActiveQuickFilter('week');
                      loadMetrics(true);
                      loadGrowth(true);
                    }}
                    className={`px-2.5 py-1.5 text-[11px] rounded-md transition-all ${
                      activeQuickFilter === 'week'
                        ? 'bg-[#32B1FF] text-white'
                        : 'bg-gray-50 dark:bg-gray-800 hover:bg-[#32B1FF] hover:text-white text-gray-600 dark:text-gray-400'
                    }`}
                  >
                    Week
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setRangeStart(fmtDate(startOfMonth(new Date())));
                      setRangeEnd(fmtDate(endOfMonth(new Date())));
                      setActiveQuickFilter('month');
                      loadMetrics(true);
                      loadGrowth(true);
                    }}
                    className={`px-2.5 py-1.5 text-[11px] rounded-md transition-all ${
                      activeQuickFilter === 'month'
                        ? 'bg-[#32B1FF] text-white'
                        : 'bg-gray-50 dark:bg-gray-800 hover:bg-[#32B1FF] hover:text-white text-gray-600 dark:text-gray-400'
                    }`}
                  >
                    Month
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const today = new Date();
                      const thirtyDaysAgo = new Date(today);
                      thirtyDaysAgo.setDate(today.getDate() - 30);
                      setRangeStart(fmtDate(thirtyDaysAgo));
                      setRangeEnd(fmtDate(today));
                      setActiveQuickFilter('30d');
                      loadMetrics(true);
                      loadGrowth(true);
                    }}
                    className={`px-2.5 py-1.5 text-[11px] rounded-md transition-all ${
                      activeQuickFilter === '30d'
                        ? 'bg-[#32B1FF] text-white'
                        : 'bg-gray-50 dark:bg-gray-800 hover:bg-[#32B1FF] hover:text-white text-gray-600 dark:text-gray-400'
                    }`}
                  >
                    30d
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const today = new Date();
                      const quarter = Math.floor(today.getMonth() / 3);
                      const startOfQuarter = new Date(today.getFullYear(), quarter * 3, 1);
                      setRangeStart(fmtDate(startOfQuarter));
                      setRangeEnd(fmtDate(today));
                      setActiveQuickFilter('quarter');
                      loadMetrics(true);
                      loadGrowth(true);
                    }}
                    className={`px-2.5 py-1.5 text-[11px] rounded-md transition-all ${
                      activeQuickFilter === 'quarter'
                        ? 'bg-[#32B1FF] text-white'
                        : 'bg-gray-50 dark:bg-gray-800 hover:bg-[#32B1FF] hover:text-white text-gray-600 dark:text-gray-400'
                    }`}
                  >
                    Quarter
                  </button>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 ml-auto">
                  {activeQuickFilter && (
                    <button
                      onClick={() => setActiveQuickFilter(null)}
                      type="button"
                      className="px-3 py-2 text-sm rounded-lg border dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors"
                      title="Clear active filter"
                    >
                      Clear Filter
                    </button>
                  )}
                  <button
                    onClick={() => { loadMetrics(true); loadGrowth(true); }}
                    type="button"
                    className="px-4 py-2 text-sm rounded-lg bg-[#32B1FF] text-white font-medium hover:bg-[#259ae6] transition-colors"
                    disabled={metricsLoading || growthLoading}
                    title="Refresh metrics (force cache refresh)"
                  >
                    {(metricsLoading || growthLoading) ? "Loading…" : "Refresh"}
                  </button>
                  <div className="relative" ref={exportRef}>
                    <button type="button" onClick={() => setExportOpen(!exportOpen)}
                      className="px-3 py-2 text-sm rounded-lg border dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100 transition-colors inline-flex items-center gap-1">
                      Export
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                    </button>
                    {exportOpen && (
                      <div className="absolute right-0 top-full mt-1 z-30 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg shadow-lg py-1 w-32">
                        <button type="button" onClick={() => { downloadCSV(); setExportOpen(false); }}
                          className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                          Export CSV
                        </button>
                        <button type="button" onClick={() => { downloadPDF(); setExportOpen(false); }}
                          className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                          Export PDF
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>}

            {/* Growth badges */}
            <div className="rounded-2xl border dark:border-gray-800 bg-white dark:bg-gray-900 p-5">
              <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Growth vs previous period</div>
              {product === "voice" ? (
                /* Voice Growth Badges */
                <div className="mt-2 flex flex-wrap gap-2">
                  <GrowthBadge label="Calls" value={growth.growth.conversations ?? 0} title="Calls growth vs. previous period" />
                  <GrowthBadge label="Transfers" value={growth.growth.transfers ?? 0} title="Transfers growth vs. previous period" />
                  <GrowthBadge label="Bookings" value={growth.growth.bookings ?? 0} title="Bookings growth vs. previous period" />
                  <GrowthBadge label="Reservations" value={growth.growth.reservations ?? 0} title="Reservations growth vs. previous period" />
                </div>
              ) : (
                /* Chat Growth Badges */
                <div className="mt-2 flex flex-wrap gap-2">
                  <GrowthBadge label="Convos" value={growth.growth.conversations ?? 0} title="Conversations growth vs. previous period" />
                  <GrowthBadge label="Msgs" value={growth.growth.messages ?? 0} title="Messages growth vs. previous period" />
                  <GrowthBadge label="Reservations" value={growth.growth.reservations ?? 0} title="Reservations growth vs. previous period" />
                </div>
              )}
              {(growthLoading || growthError) && (
                <div className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                  {growthLoading ? "Calculating growth…" : growthError}
                </div>
              )}
            </div>

            {/* Charts */}
            <div ref={chartsRef} className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <div className="rounded-xl border dark:border-gray-800 bg-white dark:bg-gray-900 p-5">
                <div className="text-sm font-semibold mb-3 text-gray-900 dark:text-gray-100">
                  {product === "voice" ? "Calls Over Time" : "Conversations vs Messages"}
                </div>
                {metricsLoading ? (
                  <div className="text-gray-500 dark:text-gray-400">Loading chart…</div>
                ) : metricsError ? (
                  <div className="text-red-600 dark:text-red-400">{metricsError}</div>
                ) : (
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        {product === "voice" ? (
                          <Line type="monotone" dataKey="conversations" name="Calls" stroke="#2563eb" dot={false} />
                        ) : (
                          <>
                            <Line type="monotone" dataKey="conversations" stroke="#2563eb" dot={false} />
                            <Line type="monotone" dataKey="messages" stroke="#16a34a" dot={false} />
                          </>
                        )}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>

              <div className="rounded-xl border dark:border-gray-800 bg-white dark:bg-gray-900 p-5">
                <div className="text-sm font-semibold mb-3 text-gray-900 dark:text-gray-100">
                  {product === "voice" ? "Reservations Over Time" : "Reservations Over Time"}
                </div>
                {metricsLoading ? (
                  <div className="text-gray-500 dark:text-gray-400">Loading chart…</div>
                ) : metricsError ? (
                  <div className="text-red-600 dark:text-red-400">{metricsError}</div>
                ) : (
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis allowDecimals={false} />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="reservations" fill="#f59e0b" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </div>

          </div>
        </main>
      </div>
    </div>
  );
}

export default DashboardPage;
